import {
  DatePicker,
  Field,
  Input,
  RadioGroup,
  Spin,
} from '@/components/commons';
import {
  FALLBACK_URL,
  FILE_SIZE_LIMIT,
  SUPPORTED_EXTENSION,
  SUPPORTED_FORMATS,
} from '@/services/image';
import { CloseOutlined } from '@ant-design/icons';
import { Badge, Button, Image, Upload } from 'antd';
import { useFormContext } from 'react-hook-form';
import { REDIRECT_TYPE_OPTIONS } from './constants';
import dayjs, { Dayjs } from 'dayjs';
import { Status, statusOptions } from '@/utils';
import moment, { Moment } from 'moment';

import { useMemo } from 'react';
import { IGetDetailGameIntroductionResponse } from '@/types/response';

interface IProps {
  isLoading: boolean;
  gameIntro?: IGetDetailGameIntroductionResponse;
}

type AttachmentFieldName = string;

type ImageFieldName = 'icon' | 'cover';

export const GameIntroductionForm: React.FC<IProps> = ({
  isLoading,
  gameIntro,
}) => {
  const { trigger, watch, setValue, setError, clearErrors } = useFormContext();

  const disabledDate = (current: Dayjs) => {
    return current.isBefore(dayjs().startOf('day'));
  };

  const disabledTime = (current: Dayjs) => {
    if (!current) return {};
    const now = dayjs();
    const selectedDate = dayjs(current);
    if (selectedDate.isBefore(now, 'day')) {
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i),
        disabledMinutes: () => Array.from({ length: 60 }, (_, i) => i),
      };
    }
    if (selectedDate.isSame(now, 'day')) {
      const disabledHours: number[] = [];
      for (let i = 0; i < now.hour(); i++) {
        disabledHours.push(i);
      }
      const disabledMinutes = (selectedHour: number) => {
        if (selectedHour === now.hour()) {
          const minutes = [];
          for (let i = 0; i <= now.minute(); i++) {
            minutes.push(i);
          }
          return minutes;
        }
        return [];
      };
      return {
        disabledHours: () => disabledHours,
        disabledMinutes: disabledMinutes,
      };
    }
    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
    };
  };
  const isDisabled = watch('isDisabled');
  const status = watch('status');

  const isPublic = useMemo(
    () =>
      !!gameIntro &&
      gameIntro.status === Status.PUBLIC &&
      moment(gameIntro.publicAt).isSameOrBefore(moment()),
    [gameIntro],
  );

  // icon
  const icon = watch('icon');
  const iconAttachments = watch('iconAttachments');

  // cover image
  const cover = watch('cover');
  const coverAttachments = watch('coverAttachments');

  const beforeUpload = (fieldName: AttachmentFieldName) => (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (file.size > FILE_SIZE_LIMIT) {
      setError(fieldName, {
        type: 'validate',
        message: '画像のサイズは10MBを超えています。',
      });
      return Upload.LIST_IGNORE;
    }

    if (
      !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
      !SUPPORTED_FORMATS.includes(file.type)
    ) {
      setError(fieldName, {
        type: 'validate',
        message: '画像は .jpg、.png、.jpeg 形式ではありません。',
      });
      return Upload.LIST_IGNORE;
    }

    clearErrors(fieldName);
    return true;
  };

  const handleUpload =
    (imageField: ImageFieldName, attachmentsField: AttachmentFieldName) =>
    (info: any) => {
      const file = info.file.originFileObj || info.file;
      const reader = new FileReader();

      reader.onload = () => {
        setValue(imageField, reader.result as string, { shouldValidate: true });
        setValue(attachmentsField, info, { shouldValidate: true });
      };

      reader.readAsDataURL(file);
    };

  const handleRemove = (
    imageField: ImageFieldName,
    attachmentsField: AttachmentFieldName,
  ) => {
    setValue(imageField, null, { shouldValidate: true });
    setValue(attachmentsField, null, { shouldValidate: true });
    clearErrors(attachmentsField);
  };

  return (
    <Spin spinning={isLoading}>
      {/* Icon */}
      {!iconAttachments?.fileList?.length && !icon && (
        <Field
          label='アイコン'
          name='iconAttachments'
          className='mb-0'
          required
        >
          <div className='border-solid rounded border-2 border-zinc-300 w-[200px] h-[100px] flex justify-center items-center'>
            <Upload
              customRequest={() => {}}
              listType='text'
              maxCount={1}
              beforeUpload={beforeUpload('iconAttachment')}
              showUploadList={false}
              onChange={handleUpload('icon', 'iconAttachments')}
              disabled={isDisabled}
            >
              <Button
                type='default'
                shape='default'
                size='middle'
                className='text-[#84ab42] border border-[#84ab42] font-semibold'
              >
                画像を選択
              </Button>
            </Upload>
          </div>
        </Field>
      )}

      {icon && (
        <Field label='アイコン' name='icon' className='mb-0' required>
          <div className='relative inline-block'>
            <Image
              height={100}
              src={icon}
              className='block rounded-lg'
              fallback={FALLBACK_URL}
              preview
            />
            {!isDisabled && (
              <Button
                type='primary'
                shape='circle'
                icon={<CloseOutlined />}
                size='small'
                onClick={() => handleRemove('icon', 'iconAttachments')}
                className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
              />
            )}
          </div>
        </Field>
      )}

      <Field name='iconAttachment' className='upload-field-error mb-0'>
        <div />
      </Field>

      <Field name='appName' label='投稿者名' className='w-full' required>
        <Input disabled={isDisabled} placeholder='投稿者名' />
      </Field>

      {/* Cover image */}
      {!coverAttachments?.fileList?.length && !cover && (
        <Field
          label='カバー画像'
          name='coverAttachments'
          className='mb-0'
          required
        >
          <div className='border-solid rounded border-2 border-zinc-300 w-[200px] h-[100px] flex justify-center items-center'>
            <Upload
              customRequest={() => {}}
              listType='text'
              maxCount={1}
              beforeUpload={beforeUpload('coverAttachment')}
              showUploadList={false}
              onChange={handleUpload('cover', 'coverAttachments')}
              disabled={isDisabled}
            >
              <Button
                type='default'
                shape='default'
                size='middle'
                className='text-[#84ab42] border border-[#84ab42] font-semibold'
              >
                画像を選択
              </Button>
            </Upload>
          </div>
        </Field>
      )}

      {cover && (
        <Field label='カバー画像' name='cover' className='mb-0' required>
          <div className='relative inline-block'>
            <Image
              height={120}
              src={cover}
              className='block rounded-lg'
              fallback={FALLBACK_URL}
              preview
            />
            {!isDisabled && (
              <Button
                type='primary'
                shape='circle'
                icon={<CloseOutlined />}
                size='small'
                onClick={() => handleRemove('cover', 'coverAttachments')}
                className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
              />
            )}
          </div>
        </Field>
      )}

      <Field name='coverAttachment' className='upload-field-error mb-0'>
        <div />
      </Field>

      <Field name='title' label='タイトル' className='w-full' required>
        <Input disabled={isDisabled} placeholder='タイトル' />
      </Field>

      <Field name='type' label='遷移先' className='w-full'>
        <RadioGroup
          options={REDIRECT_TYPE_OPTIONS as any}
          disabled={isDisabled}
        />
      </Field>

      <div className='flex items-baseline gap-2'>
        <div className='flex-1'>
          <span className='title-calendar font-semibold'>
            公開日時
            <Badge status='error' className='ml-2' />
          </span>
        </div>
        <div className='flex-1'>
          <span className='title-calendar font-semibold ml-2'>
            公開終了日時
          </span>
        </div>
      </div>
      <div className='flex items-baseline gap-1 pt-2'>
        <Field name='publicAt' className='flex-1'>
          <DatePicker
            disabledDate={disabledDate}
            disabledTime={disabledTime}
            showTime
            onCalendarChange={(e) => {
              setValue('publicAt', e ? (e as Moment).second(0) : undefined);
              trigger('publicAt');
              trigger('publicEndAt');
            }}
            disabled={isDisabled || (isPublic && status !== Status.DRAFT)}
          />
        </Field>
        <span>～</span>
        <Field name='publicEndAt' className='flex-1'>
          <DatePicker
            disabledDate={disabledDate}
            disabledTime={disabledTime}
            showTime
            onCalendarChange={(e) => {
              setValue('publicEndAt', e ? (e as Moment).second(0) : undefined);
              trigger('publicEndAt');
            }}
            disabled={isDisabled}
          />
        </Field>
      </div>

      <Field label='公開ステータス' name='status' className='w-full'>
        <RadioGroup options={statusOptions} disabled={isDisabled} />
      </Field>
    </Spin>
  );
};
